
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Camera, GripVertical, Plus, Sparkles, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface TrayItem {
  id: string;
  preview: string;
  name: string;
}

interface PhotoTrayProps {
  items: TrayItem[];
  onReorder: (next: TrayItem[]) => void;
  onRemove: (id: string) => void;
  onAddMorePhoto: () => void;
  onAddMoreUpload: () => void;
  showCameraButton?: boolean;
  onClear: () => void;
  onConfirm: () => void;
  maxItems?: number;
}

const SortableThumb = ({
  item,
  index,
  onRemove,
}: {
  item: TrayItem;
  index: number;
  onRemove: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-border bg-muted shadow-soft"
    >
      <img
        src={item.preview}
        alt={`Page ${index + 1}: ${item.name}`}
        className="h-full w-full object-cover"
        draggable={false}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" />
      <span className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground shadow-soft">
        Page {index + 1}
      </span>
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        aria-label={`Remove page ${index + 1}`}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow-soft transition-smooth hover:bg-destructive hover:text-destructive-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Drag page ${index + 1} to reorder`}
        className="absolute bottom-2 right-2 flex h-8 w-8 cursor-grab items-center justify-center rounded-full bg-background/90 text-foreground shadow-soft active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
    </div>
  );
};

export const PhotoTray = ({
  items,
  onReorder,
  onRemove,
  onAddMorePhoto,
  onAddMoreUpload,
  showCameraButton = false,
  onClear,
  onConfirm,
  maxItems = 8,
}: PhotoTrayProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <section className="mx-auto max-w-3xl px-6 pb-24 pt-10 sm:pt-16">
      <div className="animate-fade-up rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
              Order your pages
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Drag to put them in the right order, then clean them up.
            </p>
          </div>
          <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
            {items.length} / {maxItems}
          </span>
        </div>

        <div className="mt-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {items.map((item, idx) => (
                  <SortableThumb
                    key={item.id}
                    item={item}
                    index={idx}
                    onRemove={onRemove}
                  />
                ))}
                {items.length < maxItems && (
                  <div className="flex aspect-[3/4] flex-col gap-2 rounded-xl border-2 border-dashed border-border bg-background/60 p-2">
                    {showCameraButton && (
                      <button
                        type="button"
                        onClick={onAddMorePhoto}
                        className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-lg text-muted-foreground transition-smooth hover:bg-primary/5 hover:text-primary"
                      >
                        <Camera className="h-5 w-5" />
                        <span className="text-xs font-medium">Take photo</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onAddMoreUpload}
                      className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-lg text-muted-foreground transition-smooth hover:bg-primary/5 hover:text-primary"
                    >
                      {showCameraButton ? (
                        <Upload className="h-5 w-5" />
                      ) : (
                        <Plus className="h-6 w-6" />
                      )}
                      <span className="text-xs font-medium">
                        {showCameraButton ? "Upload" : "Add more"}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <Button onClick={onConfirm} className="h-11 sm:flex-1" disabled={items.length === 0}>
            <Sparkles className="h-4 w-4" />
            Clean {items.length} {items.length === 1 ? "photo" : "photos"} in this order
          </Button>
          <Button variant="ghost" onClick={onClear} className="h-11 sm:w-auto">
            <Trash2 className="h-4 w-4" />
            Clear all
          </Button>
        </div>
      </div>
    </section>
  );
};
